"use client";

import Image from "next/image";
import Link from "next/link";
import { useEffect, useState } from "react";
import parse from "html-react-parser";
import { motion, useReducedMotion, useScroll, useSpring } from "motion/react";
import { ArrowLeft, ArrowRight, Calendar, Clock, Link2, Check, Share2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { postHref, type BlogPost } from "@/lib/blog";

// ── Reading progress bar ──────────────────────────────────────────────────────

function ReadingProgress() {
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, {
    stiffness: 120,
    damping: 30,
    mass: 0.4,
  });
  return (
    <motion.div
      style={{ scaleX, top: "var(--header-height, 64px)" }}
      className="fixed left-0 right-0 z-[150] h-[3px] origin-left bg-accent-500"
      aria-hidden="true"
    />
  );
}

// ── Share row ─────────────────────────────────────────────────────────────────

function ShareRow({ title }: { title: string }) {
  const [copied, setCopied] = useState(false);

  async function copyLink() {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // clipboard unavailable — no op
    }
  }

  async function share() {
    if (typeof navigator !== "undefined" && navigator.share) {
      try {
        await navigator.share({ title, url: window.location.href });
      } catch {
        // dismissed — no op
      }
    } else {
      copyLink();
    }
  }

  return (
    <div className="flex items-center gap-3">
      <span className="text-sm font-medium text-text-muted">Share</span>
      <button
        type="button"
        onClick={copyLink}
        className="inline-flex items-center gap-2 rounded-full border border-[#e3ecfb] bg-white px-4 py-2 text-sm font-medium text-primary-700 transition-colors hover:border-primary-200 hover:bg-primary-50/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600/40"
      >
        {copied ? (
          <Check className="h-4 w-4 text-success" aria-hidden="true" />
        ) : (
          <Link2 className="h-4 w-4" aria-hidden="true" />
        )}
        {copied ? "Copied" : "Copy link"}
      </button>
      <button
        type="button"
        onClick={share}
        className="inline-flex items-center gap-2 rounded-full bg-primary-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600/40"
      >
        Share article
      </button>
    </div>
  );
}

// ── Body ──────────────────────────────────────────────────────────────────────
// `post.excerpt` is shown as a styled intro line, and the full CMS-authored
// HTML body (`post.content`) is parsed and rendered below it.

function ArticleBody({ post }: { post: BlogPost }) {
  return (
    <div className="blog-prose">
      {post.excerpt ? <p className="lead">{post.excerpt}</p> : null}
      {post.content ? parse(post.content) : null}
    </div>
  );
}

// ── Related card ──────────────────────────────────────────────────────────────

function RelatedCard({ post }: { post: BlogPost }) {
  return (
    <Link
      href={postHref(post)}
      aria-label={post.title}
      className="group flex flex-col overflow-hidden rounded-card border border-[#e3ecfb] bg-white transition-all duration-300 hover:-translate-y-1 hover:border-primary-200 hover:shadow-[0_18px_40px_-24px_rgba(10,45,110,0.28)]"
    >
      <div className="relative h-40 w-full overflow-hidden">
        <Image
          src={post.image}
          alt={post.title}
          fill
          sizes="(min-width: 1024px) 33vw, 100vw"
          className="object-cover transition-transform duration-500 group-hover:scale-105"
        />
      </div>
      <div className="flex flex-1 flex-col gap-2 p-5">
        <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-primary-500">
          {post.category}
        </span>
        <h3 className="line-clamp-2 text-base font-bold leading-snug text-primary-900">
          {post.title}
        </h3>
        <span className="mt-auto inline-flex items-center gap-1 pt-2 text-sm font-semibold text-primary-600 transition-colors group-hover:text-primary-800">
          Read
          <ArrowRight
            className="h-3.5 w-3.5 transition-transform duration-200 group-hover:translate-x-0.5"
            aria-hidden="true"
          />
        </span>
      </div>
    </Link>
  );
}

// ── Article ───────────────────────────────────────────────────────────────────

export function BlogArticle({
  post,
  related,
}: {
  post: BlogPost;
  related: BlogPost[];
}) {
  const reduce = useReducedMotion() ?? false;
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  return (
    <article className="bg-neutral-0 pb-20 lg:pb-0">
      {mounted ? <ReadingProgress /> : null}

      <div className="mx-auto max-w-[820px] px-5 pt-10 sm:px-8 sm:pt-14 lg:pt-16">
        {/* Back link */}
        <Link
          href="/blogs"
          className="inline-flex items-center gap-1.5 text-sm font-medium text-primary-600 transition-colors hover:text-primary-800"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          All articles
        </Link>

        {/* Meta */}
        <motion.div
          initial={reduce ? false : { opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: [0.2, 0, 0, 1] }}
          className="mt-6"
        >
          <div className="flex flex-wrap items-center gap-3">
            <span className="inline-flex items-center rounded-full bg-primary-50 px-3 py-1 text-xs font-semibold text-primary-700">
              {post.category}
            </span>
            <span className="inline-flex items-center gap-1.5 text-sm text-text-muted">
              <Calendar className="h-4 w-4" aria-hidden="true" />
              {post.date}
            </span>
            <span className="inline-flex items-center gap-1.5 text-sm text-text-muted">
              <Clock className="h-4 w-4" aria-hidden="true" />5 min read
            </span>
          </div>

          <h1 className="mt-5 text-[clamp(1.9rem,4vw,2.9rem)] font-semibold leading-[1.12] tracking-[-0.015em] text-primary-900">
            {post.title}
          </h1>
        </motion.div>
      </div>

      {/* Cover image */}
      <motion.div
        initial={reduce ? false : { opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.2, 0, 0, 1], delay: 0.08 }}
        className="mx-auto mt-8 max-w-[980px] px-5 sm:px-8"
      >
        <div className="relative aspect-[16/9] w-full overflow-hidden rounded-card border border-[#e3ecfb]">
          <Image
            src={post.image}
            alt={post.title}
            fill
            sizes="(min-width: 980px) 980px, 100vw"
            className="object-cover"
            priority
          />
        </div>
      </motion.div>

      {/* Body */}
      <div className="mx-auto max-w-[720px] px-5 py-12 sm:px-8 sm:py-14">
        <ArticleBody post={post} />

        {/* Share */}
        <div className="mt-12 border-t border-[#e3ecfb] pt-8">
          <ShareRow title={post.title} />
        </div>
      </div>

      {/* Related */}
      {related.length > 0 ? (
        <section
          className="border-t border-[#e3ecfb] bg-[#f7faff] py-16 sm:py-20"
          aria-labelledby="related-heading"
        >
          <div className="mx-auto max-w-[1200px] px-5 sm:px-8">
            <div className="mb-8 flex items-end justify-between gap-4">
              <h2
                id="related-heading"
                className="text-[clamp(1.4rem,2.6vw,1.9rem)] font-semibold tracking-[-0.015em] text-primary-900"
              >
                Keep reading
              </h2>
              <Link
                href="/blogs"
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary-600 transition-colors hover:text-primary-800"
              >
                All articles
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Link>
            </div>
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {related.map((p) => (
                <RelatedCard key={p.slug} post={p} />
              ))}
            </div>
          </div>
        </section>
      ) : null}

      {/* Mobile-only fixed action bar — back and share always reachable */}
      <MobileActionBar title={post.title} />
    </article>
  );
}

// ── Mobile action bar (fixed to viewport bottom on <lg) ──────────────────────

function MobileActionBar({ title }: { title: string }) {
  const [copied, setCopied] = useState(false);

  async function share() {
    const url = typeof window !== "undefined" ? window.location.href : "";
    if (typeof navigator !== "undefined" && navigator.share) {
      try {
        await navigator.share({ title, url });
        return;
      } catch {
        // fall through to copy
      }
    }
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // no op
    }
  }

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-[140] border-t border-[#e3ecfb] bg-white/95 backdrop-blur-md shadow-[0_-8px_24px_-14px_rgba(10,45,110,0.18)] lg:hidden"
      style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
    >
      <div className="flex items-center gap-2 px-4 py-3">
        <Link
          href="/blogs"
          className="inline-flex flex-1 items-center justify-center gap-2 rounded-full border border-[#e3ecfb] bg-white px-4 py-3 text-sm font-semibold text-primary-700 transition-colors hover:border-primary-200 hover:bg-primary-50/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600/40"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          All articles
        </Link>
        <button
          type="button"
          onClick={share}
          aria-label="Share this article"
          className="inline-flex flex-1 items-center justify-center gap-2 rounded-full bg-primary-600 px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-primary-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600/40"
        >
          {copied ? (
            <>
              <Check className="h-4 w-4" aria-hidden="true" />
              Link copied
            </>
          ) : (
            <>
              <Share2 className="h-4 w-4" aria-hidden="true" />
              Share
            </>
          )}
        </button>
      </div>
    </div>
  );
}
